"use client";

import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../ui/components/Button';
import { IconButton } from '../../ui/components/IconButton';
import { FeatherZap, FeatherShoppingCart, FeatherCheck } from '@subframe/core';
import { formatPrice, formatPriceRounded } from '../../utils/formatters';
import { is3DModel } from '../../utils/media';
import { RarityRank } from './RarityRank';
import { PointsBadge } from './PointsBadge';
import type { DealNFT, DealsSortBy } from '../../api/deals';

interface DealCardProps {
  nft: DealNFT;
  onBuyClick?: (nft: DealNFT, event: React.MouseEvent) => void;
  isAuthenticated?: boolean;
  sortBy?: DealsSortBy;
  onAddToCart?: (nft: DealNFT, event: React.MouseEvent) => void;
  isInCart?: boolean;
}

/**
 * Get the best available image URL, handling IPFS URLs
 */
const getImageUrl = (nft: DealNFT): string => {
  const url = nft.thumbnail_url || nft.media_url || '';

  // Convert IPFS URLs to gateway URLs
  if (url.startsWith('ipfs://')) {
    return url.replace('ipfs://', 'https://ipfs.io/ipfs/');
  }

  return url;
};

/**
 * DealCard - Card component for displaying NFT deals
 *
 * Shows:
 * - NFT image
 * - NFT name
 * - Collection name (linked)
 * - Rarity rank
 * - Estimated value range
 * - Listing price
 * - Discount badge
 * - Buy Now button
 */
export const DealCard: React.FC<DealCardProps> = ({
  nft,
  onBuyClick,
  isAuthenticated = false,
  sortBy = 'xrp',
  onAddToCart,
  isInCart = false,
}) => {
  const imageUrl = getImageUrl(nft);
  const is3D = is3DModel(nft);

  const handleBuyClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (onBuyClick) {
      onBuyClick(nft, event);
    }
  };

  const handleAddToCart = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (onAddToCart) {
      onAddToCart(nft, event);
    }
  };

  // Format the estimated value (rounded to whole number)
  const estimatedValueRounded = Math.round(nft.estimated_value);

  // Format the discount amount in XRP
  const discountXRP = Math.round(nft.discount_amount);

  return (
    <Link
      to={`/nfts/${nft.nft_token_id}`}
      className="flex grow shrink-0 basis-0 flex-col items-start overflow-hidden rounded-md border border-solid border-neutral-border bg-default-background shadow-sm hover:shadow-md transition-shadow h-full"
    >
      {/* Image Container */}
      <div className="relative w-full">
        {is3D && nft.thumbnail_url ? (
          <img
            className="w-full flex-none aspect-square object-cover"
            src={nft.thumbnail_url}
            alt={nft.name || '3D NFT'}
          />
        ) : is3D ? (
          <div className="w-full flex-none aspect-square bg-gradient-to-br from-neutral-100 to-neutral-200 flex flex-col items-center justify-center gap-1">
            <span className="text-3xl">🧊</span>
            <span className="text-xs font-medium text-neutral-500">3D Model</span>
          </div>
        ) : imageUrl ? (
          <img
            className="w-full flex-none aspect-square object-cover"
            src={imageUrl}
            alt={nft.name || 'NFT'}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const placeholder = e.currentTarget.nextElementSibling;
              if (placeholder) {
                placeholder.classList.remove('hidden');
              }
            }}
          />
        ) : null}
        {/* Fallback placeholder */}
        <div
          className={`w-full flex-none aspect-square bg-neutral-100 flex items-center justify-center ${imageUrl || is3D ? 'hidden' : ''}`}
        >
          <span className="text-heading-2 font-heading-2 text-neutral-400">NFT</span>
        </div>

        {/* 3D badge */}
        {is3D && (
          <div className="absolute top-2 left-2 bg-black/70 text-white text-xs font-semibold px-1.5 py-0.5 rounded flex items-center gap-1">
            <span style={{ fontSize: '10px' }}>🧊</span> 3D
          </div>
        )}

        {/* Discount Badge - shows XRP or percentage based on sort */}
        <div className="absolute top-2 right-2 flex items-center gap-1 rounded-full bg-gradient-to-r from-green-600 to-green-500 px-2 py-1 shadow-md">
          <FeatherZap className="h-3 w-3 text-white fill-white" />
          <span className="text-xs font-semibold text-white">
            {sortBy === 'percentage'
              ? `${Math.round(nft.discount_percentage)}% below value`
              : `${discountXRP} XRP below value`}
          </span>
        </div>

        {/* Points Badge */}
        {nft.collection_points != null && nft.collection_points > 0 && (
          <PointsBadge points={nft.collection_points} />
        )}
      </div>

      {/* Content */}
      <div className="flex w-full flex-col items-start gap-2 px-4 py-4 flex-grow">
        {/* Name */}
        <span className="text-body-bold font-body-bold text-default-font truncate w-full">
          {nft.name || `NFT #${nft.nft_token_id.slice(0, 8)}...`}
        </span>

        {/* Collection Name */}
        <Link
          to={`/collections/${nft.collection_id}`}
          onClick={(e) => e.stopPropagation()}
          className="text-caption font-caption text-subtext-color hover:text-brand-700 truncate w-full"
        >
          {nft.collection_name}
        </Link>

        {/* Rarity Rank */}
        {nft.rarity_rank && nft.total_supply && (
          <RarityRank
            rank={nft.rarity_rank}
            totalSupply={nft.total_supply}
            className="text-caption font-caption"
          />
        )}

        {/* Estimated Value */}
        <div className="flex items-center gap-1 text-caption">
          <span className="text-subtext-color">Est. Value:</span>
          <span className="text-default-font">{estimatedValueRounded} XRP</span>
        </div>

        {/* Listed Price - on same line */}
        <div className="flex items-center gap-1 text-caption">
          <span className="text-subtext-color">Listed:</span>
          <span className="text-body-bold font-body-bold text-brand-700">
            {formatPrice(nft.min_sell_price)}
          </span>
        </div>

        {/* Buy Now + Cart Button */}
        {onBuyClick && isAuthenticated && (
          <div className="flex w-full items-center justify-center gap-2 mt-auto pt-2">
            <Button
              size="medium"
              onClick={handleBuyClick}
            >
              Buy Now
            </Button>
            {onAddToCart && (
              <IconButton
                size="medium"
                variant={isInCart ? 'brand-secondary' : 'neutral-tertiary'}
                icon={isInCart ? <FeatherCheck className="h-4 w-4" /> : <FeatherShoppingCart className="h-4 w-4" />}
                disabled={isInCart}
                onClick={handleAddToCart}
              />
            )}
          </div>
        )}
      </div>
    </Link>
  );
};
